/**
 * The document AI layer's wire vocabulary (slate-feature-transfer W8 §A).
 *
 * ONE aggregate (`DocumentThought`), TWO controllers, two projections and no shared type on
 * the server — so this file is where the two faces are reconciled into one shape the UI can
 * hold. Everything here is BARE on the wire (§0.2): no `{data}`, no `{data, policies}`, and
 * therefore no abilities either (§0.3) — the drawer gates on the HOST DOCUMENT's policy and
 * never derives a permission from a conversation payload.
 *
 * Three rules this file exists to enforce:
 *  1. `role` is derived ONCE, here, from `authorType` + `messageType` + metadata — never in a
 *     component (kms had five incompatible role vocabularies; §A.1).
 *  2. `metadata` is jsonb with no server-side schema, so it is read through a discriminated
 *     view keyed on `messageType`. An unknown type keeps its raw object and renders plainly;
 *     it never throws.
 *  3. camelCase only. kms carried `x ?? X` dual reads that masked two real defects (D4/D5);
 *     the PascalCase fallbacks are deliberately absent.
 */
import { z } from "zod";

// ── Message taxonomy ─────────────────────────────────────────────────────────

/** The real (lowercase snake) taxonomy, server- and client-written. §A.1 */
export const MESSAGE_TYPES = [
  "text",
  "prompt",
  "prompt_run",
  "context_item",
  "context_snapshot",
  "context_config",
  "derived_artifact",
  "conversation_status",
  "message_revision",
  "chain_refresh",
  "moderator_synthesis",
  "moderator_evaluation",
  "moderator_reinstruction",
  "dropped",
  "conversation_merge",
  "config",
  "moderator_review",
  "moderator_merge",
  "transform",
] as const;
export type MessageType = (typeof MESSAGE_TYPES)[number];

/**
 * What the LIFECYCLE face hides and the TURN face returns raw. The drawer reads the turn face
 * (F.4 decision 2) and filters here, because the control rows ARE the context pack, the
 * artifacts and the committed flag — fetching twice would be the expensive way to lose them.
 */
export const NON_TURN_MESSAGE_TYPES = [
  "dropped",
  "conversation_merge",
  "context_item",
  "context_snapshot",
  "context_config",
  "prompt_run",
  "derived_artifact",
  "conversation_status",
  "message_revision",
  "chain_refresh",
  "moderator_evaluation",
  "moderator_reinstruction",
] as const;

const NON_TURN = new Set<string>(NON_TURN_MESSAGE_TYPES);

/** True for the control rows the transcript must not render as turns. */
export function isTurnMessage(messageType: string): boolean {
  return !NON_TURN.has(messageType);
}

// ── Roles ────────────────────────────────────────────────────────────────────

export const AUTHOR_TYPES = ["human", "ai", "system"] as const;
export type AuthorType = (typeof AUTHOR_TYPES)[number];

/** Slate's ONE role vocabulary. Derived in the adapter, never re-derived in a component. */
export const CONVERSATION_ROLES = ["user", "thinker", "reviewer", "moderator"] as const;
export type ConversationRole = (typeof CONVERSATION_ROLES)[number];

const MODERATOR_TYPES = new Set([
  "moderator_synthesis",
  "moderator_evaluation",
  "moderator_reinstruction",
  "moderator_merge",
]);

/**
 * The role table (§A.1). `merged_response` arrives as an ordinary `ai` row carrying
 * `metadata.isSynthesis`/`mergedFrom` — which is exactly how kms lost moderator attribution
 * (W8-9): it looked for a `moderator_synthesis` event the server never emits.
 */
export function deriveRole(
  authorType: string,
  messageType: string,
  metadata: Record<string, unknown> | null | undefined,
): ConversationRole {
  if (messageType === "moderator_review") {
    return "reviewer";
  }
  if (MODERATOR_TYPES.has(messageType)) {
    return "moderator";
  }
  const meta = metadata ?? {};
  if (meta.isSynthesis === true || meta.isModerator === true || meta.mergedFrom !== undefined) {
    return "moderator";
  }
  if (authorType === "ai") {
    return "thinker";
  }
  if (authorType === "system") {
    return "reviewer";
  }
  return "user";
}

// ── Metadata: a discriminated VIEW over a schema-less jsonb column ────────────

const RawMetadataSchema = z.record(z.string(), z.unknown()).nullable().default(null);
export type RawMetadata = Record<string, unknown> | null;

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];

/**
 * The typed views. `kind` is the discriminator; `raw` is always kept so nothing is lost and an
 * unknown `messageType` still renders. This is the single highest-leverage typing decision in
 * the wave (§A.1) — every consumer reads a field that exists rather than probing jsonb.
 */
export type MessageMetadata =
  | {
      kind: "ai";
      thinkerId: string | null;
      model: string | null;
      streamId: string | null;
      tokensUsed: number | null;
      durationMs: number | null;
      isSynthesis: boolean;
      mergedFrom: string[];
      responsesCount: number | null;
      raw: RawMetadata;
    }
  | { kind: "config"; thinkerIds: string[]; thinkerNames: string[]; raw: RawMetadata }
  | {
      kind: "moderator_review";
      reviewId: string | null;
      targetMessageId: string | null;
      selection: string | null;
      rating: number | null;
      action: string | null;
      raw: RawMetadata;
    }
  | {
      kind: "moderator_merge";
      targetMessageId: string | null;
      confidence: number | null;
      reviewCount: number | null;
      status: string | null;
      raw: RawMetadata;
    }
  | {
      kind: "conversation_status";
      status: string | null;
      committedAt: string | null;
      artifactId: string | null;
      raw: RawMetadata;
    }
  | {
      kind: "context";
      contextSnapshotId: string | null;
      contextItemIds: string[];
      contextBytes: number | null;
      raw: RawMetadata;
    }
  | { kind: "dropped"; droppedFrom: string | null; droppedAt: string | null; raw: RawMetadata }
  | {
      kind: "transform";
      parentCommentId: string | null;
      thinkerId: string | null;
      thinkerName: string | null;
      updatedText: string | null;
      diff: string | null;
      raw: RawMetadata;
    }
  | { kind: "plain"; raw: RawMetadata };

/** Revision/staleness badges ride EVERY message type, so they are read separately. */
export interface MessageFlags {
  revision: number | null;
  revisedAt: string | null;
  revisedByName: string | null;
  isStale: boolean;
  staleAt: string | null;
  inReplyTo: string | null;
  facets: string[];
}

export function readFlags(metadata: RawMetadata): MessageFlags {
  const meta = metadata ?? {};
  const facet = str(meta.facet);
  return {
    revision: num(meta.revision),
    revisedAt: str(meta.revisedAt),
    revisedByName: str(meta.revisedByName),
    isStale: meta.isStale === true,
    staleAt: str(meta.staleAt),
    inReplyTo: str(meta.inReplyTo),
    facets: [...strings(meta.facets), ...strings(meta.facetTags), ...(facet ? [facet] : [])],
  };
}

/** Never throws. An unknown `messageType` lands on `plain` with its raw object intact. */
export function readMetadata(messageType: string, metadata: RawMetadata): MessageMetadata {
  const meta = metadata ?? {};
  switch (messageType) {
    case "config":
      return {
        kind: "config",
        thinkerIds: strings(meta.thinkerIds),
        thinkerNames: strings(meta.thinkerNames),
        raw: metadata,
      };
    case "moderator_review":
      return {
        kind: "moderator_review",
        reviewId: str(meta.reviewId),
        targetMessageId: str(meta.targetMessageId),
        selection: str(meta.selection),
        rating: num(meta.rating),
        action: str(meta.action),
        raw: metadata,
      };
    case "moderator_merge":
      return {
        kind: "moderator_merge",
        targetMessageId: str(meta.targetMessageId),
        confidence: num(meta.confidence),
        reviewCount: num(meta.reviewCount),
        status: str(meta.status),
        raw: metadata,
      };
    case "conversation_status":
      return {
        kind: "conversation_status",
        status: str(meta.status),
        committedAt: str(meta.committedAt),
        artifactId: str(meta.artifactId),
        raw: metadata,
      };
    case "context_item":
    case "context_snapshot":
    case "context_config":
      return {
        kind: "context",
        contextSnapshotId: str(meta.contextSnapshotId),
        contextItemIds: strings(meta.contextItemIds),
        contextBytes: num(meta.contextBytes),
        raw: metadata,
      };
    case "dropped":
      return {
        kind: "dropped",
        droppedFrom: str(meta.droppedFrom),
        droppedAt: str(meta.droppedAt),
        raw: metadata,
      };
    case "transform":
      return {
        kind: "transform",
        parentCommentId: str(meta.parentCommentId),
        thinkerId: str(meta.thinkerId),
        thinkerName: str(meta.thinkerName),
        updatedText: str(meta.updatedText),
        diff: str(meta.diff),
        raw: metadata,
      };
    default:
      break;
  }
  if (meta.thinkerId !== undefined || meta.model !== undefined || meta.streamId !== undefined) {
    return {
      kind: "ai",
      thinkerId: str(meta.thinkerId),
      model: str(meta.model),
      streamId: str(meta.streamId),
      tokensUsed: num(meta.tokensUsed),
      durationMs: num(meta.durationMs),
      isSynthesis: meta.isSynthesis === true,
      mergedFrom: strings(meta.mergedFrom ?? meta.synthesizedFrom),
      responsesCount: num(meta.responsesCount),
      raw: metadata,
    };
  }
  return { kind: "plain", raw: metadata };
}

// ── Message ──────────────────────────────────────────────────────────────────

const WireMessageSchema = z.object({
  id: z.string(),
  content: z.string().default(""),
  author: z.string().nullable().default(null),
  authorType: z.string().default("human"),
  messageType: z.string().default("text"),
  createdAt: z.string().nullable().default(null),
  editedAt: z.string().nullable().default(null),
  isEdited: z.boolean().default(false),
  metadata: RawMetadataSchema,
  /** The lifecycle face also emits the IDE-shaped `role` — kept only to be ignored. */
  role: z.string().nullable().default(null),
});

/** The canonical slate message. `role` here is SLATE's, never the wire's PascalCase one. */
export interface ConversationMessage {
  id: string;
  conversationId: string;
  author: string;
  authorType: AuthorType;
  role: ConversationRole;
  messageType: string;
  content: string;
  createdAt: string | null;
  editedAt: string | null;
  isEdited: boolean;
  metadata: MessageMetadata;
  flags: MessageFlags;
  /** Untouched jsonb, for the writes that must echo what they read. */
  raw: RawMetadata;
}

const asAuthorType = (value: string): AuthorType =>
  value === "ai" || value === "system" ? value : "human";

export function parseMessage(input: unknown, conversationId: string): ConversationMessage {
  const wire = WireMessageSchema.parse(input);
  const authorType = asAuthorType(wire.authorType.toLowerCase());
  return {
    id: wire.id,
    conversationId,
    author: wire.author ?? "",
    authorType,
    role: deriveRole(authorType, wire.messageType, wire.metadata),
    messageType: wire.messageType,
    content: wire.content,
    createdAt: wire.createdAt,
    editedAt: wire.editedAt,
    isEdited: wire.isEdited,
    metadata: readMetadata(wire.messageType, wire.metadata),
    flags: readFlags(wire.metadata),
    raw: wire.metadata,
  };
}

// ── Anchors ──────────────────────────────────────────────────────────────────

export const AnchorSchema = z.object({
  id: z.string().default(""),
  paragraphId: z.string().nullable().default(null),
  startOffset: z.number().nullable().default(null),
  endOffset: z.number().nullable().default(null),
  selectedText: z.string().nullable().default(null),
  /** Present ONLY in `GET thoughts/{id}` — absent from the list projection. */
  context: z.string().nullable().default(null),
  anchorType: z.string().default("document"),
});
export type Anchor = z.infer<typeof AnchorSchema>;

// ── Conversation ─────────────────────────────────────────────────────────────

export const CONVERSATION_STATUSES = ["Active", "Archived", "Deleted"] as const;
export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

/** `Low=0 Normal=1 High=2 Critical=3`. An INT on the wire — a string 400s (W8-27). */
export const THOUGHT_PRIORITIES = ["Low", "Normal", "High", "Critical"] as const;
export type ThoughtPriority = (typeof THOUGHT_PRIORITIES)[number];

export function priorityName(value: number): ThoughtPriority {
  return THOUGHT_PRIORITIES[value] ?? "Normal";
}
export function priorityValue(name: ThoughtPriority): number {
  const index = THOUGHT_PRIORITIES.indexOf(name);
  return index === -1 ? 1 : index;
}

/**
 * One row for both faces. Fields the turn face does not project stay null rather than being
 * invented — a summary read from `thoughts/document/{id}` genuinely does not know whether the
 * conversation was deleted, and pretending otherwise is how a UI starts lying.
 */
export interface ConversationSummary {
  id: string;
  documentId: string;
  documentTitle: string | null;
  title: string;
  createdBy: string;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  /** The LIFECYCLE status. Orthogonal to `committed` — see `isCommitted`. */
  status: ConversationStatus | null;
  isResolved: boolean;
  priority: ThoughtPriority;
  tags: string[];
  thinkerIds: string[];
  messageCount: number;
  lastMessagePreview: string | null;
  flowId: string | null;
  flowName: string | null;
  /** `CompanionDocumentId` renamed on the wire (§0.1). */
  companionDocumentId: string | null;
  anchor: Anchor | null;
}

export interface Conversation extends ConversationSummary {
  messages: ConversationMessage[];
  anchors: Anchor[];
  /** The SECOND, orthogonal status: `committed` (frozen with a digest) vs `active`. */
  isCommitted: boolean;
  committedAt: string | null;
}

const numberOr = (value: unknown, fallback: number): number =>
  typeof value === "number" ? value : fallback;

/** The last `conversation_status` row wins — `/commit` writes `committed`, `/reopen` `active`. */
export function readCommitState(messages: ConversationMessage[]): {
  isCommitted: boolean;
  committedAt: string | null;
} {
  let isCommitted = false;
  let committedAt: string | null = null;
  for (const message of messages) {
    if (message.metadata.kind !== "conversation_status") {
      continue;
    }
    isCommitted = message.metadata.status === "committed";
    committedAt = isCommitted ? (message.metadata.committedAt ?? message.createdAt) : null;
  }
  return { isCommitted, committedAt };
}

/** The TURN face's summary/detail projection (bare, camelCase, priority as an int). */
export function parseTurnSummary(input: unknown): ConversationSummary {
  const row = (input ?? {}) as Record<string, unknown>;
  const anchors = Array.isArray(row.anchors) ? row.anchors.map((a) => AnchorSchema.parse(a)) : [];
  return {
    id: String(row.id ?? ""),
    documentId: String(row.documentId ?? ""),
    documentTitle: str(row.documentTitle),
    title: typeof row.title === "string" ? row.title : "",
    createdBy: typeof row.createdBy === "string" ? row.createdBy : "",
    createdAt: str(row.createdAt),
    updatedAt: str(row.updatedAt),
    deletedAt: null,
    status: null,
    isResolved: row.isResolved === true,
    priority: priorityName(numberOr(row.priority, 1)),
    tags: strings(row.tags),
    thinkerIds: strings(row.thinkerIds ?? row.assignedThinkerIds),
    messageCount: numberOr(row.messageCount, 0),
    /** The turn face's `lastMessage` is the message CONTENT, a string (D-8). */
    lastMessagePreview: str(row.lastMessage),
    flowId: str(row.flowId),
    flowName: str(row.flowName),
    companionDocumentId: str(row.knowItDocumentId ?? row.companionDocumentId),
    anchor: anchors[0] ?? null,
  };
}

const asStatus = (value: unknown): ConversationStatus | null =>
  value === "Active" || value === "Archived" || value === "Deleted" ? value : null;

/**
 * The LIFECYCLE face's `ProjectSummary`. Its `lastMessage` is a whole `ProjectMessage` OBJECT,
 * not a string — the one shape difference between the faces that a shared parser must not
 * paper over (the W4 IDE schema reads it as a string; see the wave record).
 */
export function parseLifecycleSummary(input: unknown): ConversationSummary {
  const row = (input ?? {}) as Record<string, unknown>;
  const last = (row.lastMessage ?? null) as Record<string, unknown> | null;
  const anchorType = str(row.anchorType);
  const anchorRef = str(row.anchorRef);
  const selectedText = str(row.selectedText);
  const status = asStatus(row.status);
  return {
    id: String(row.id ?? ""),
    documentId: String(row.documentId ?? row.attachedDocumentId ?? ""),
    documentTitle: str(row.documentTitle),
    title: typeof row.title === "string" ? row.title : "",
    createdBy: typeof row.createdBy === "string" ? row.createdBy : "",
    createdAt: str(row.createdAt),
    updatedAt: str(row.updatedAt),
    deletedAt: str(row.deletedAt),
    status,
    isResolved: status === "Archived",
    priority: "Normal",
    tags: [],
    thinkerIds: strings(row.thinkerIds),
    messageCount: numberOr(row.messageCount, 0),
    lastMessagePreview: last ? str(last.content) : null,
    flowId: str(row.flowId),
    flowName: str(row.flowName),
    companionDocumentId: str(row.knowItDocumentId),
    anchor:
      anchorType || anchorRef || selectedText
        ? AnchorSchema.parse({
            anchorType: anchorType ?? "document",
            paragraphId: anchorRef,
            selectedText,
          })
        : null,
  };
}

export function parseConversation(
  input: unknown,
  face: "turn" | "lifecycle" = "turn",
): Conversation {
  const row = (input ?? {}) as Record<string, unknown>;
  const summary = face === "turn" ? parseTurnSummary(row) : parseLifecycleSummary(row);
  const messages = (Array.isArray(row.messages) ? row.messages : []).map((entry) =>
    parseMessage(entry, summary.id),
  );
  const anchors = Array.isArray(row.anchors) ? row.anchors.map((a) => AnchorSchema.parse(a)) : [];
  return {
    ...summary,
    anchors: anchors.length > 0 ? anchors : summary.anchor ? [summary.anchor] : [],
    messages,
    ...readCommitState(messages),
  };
}

// ── Context pack (S6) ────────────────────────────────────────────────────────

export const CONTEXT_AGGRESSIVENESS = ["conservative", "balanced", "proactive"] as const;
export type ContextAggressiveness = (typeof CONTEXT_AGGRESSIVENESS)[number];

export const CONTEXT_ITEM_TYPES = ["doc_anchor", "code", "url", "note"] as const;
export type ContextItemType = (typeof CONTEXT_ITEM_TYPES)[number];

export const CONTEXT_STATUSES = ["active", "suggested", "removed"] as const;
export type ContextStatus = (typeof CONTEXT_STATUSES)[number];

/** 100 KiB, enforced with `400 {"error":"context_limit_exceeded","maxBytes":102400}`. */
export const CONTEXT_MAX_BYTES = 102_400;

export const ContextItemSchema = z.object({
  id: z.string(),
  type: z.string().default("note"),
  title: z.string().default("Context item"),
  content: z.string().default(""),
  source: z.record(z.string(), z.unknown()).nullable().default(null),
  facetTags: z.array(z.string()).default([]),
  audience: z.record(z.string(), z.unknown()).nullable().default(null),
  addedBy: z.string().default("user"),
  status: z.string().default("active"),
  reason: z.string().nullable().default(null),
  createdById: z.string().nullable().default(null),
  createdAt: z.string().nullable().default(null),
  contentBytes: z.number().default(0),
});
export type ContextItem = z.infer<typeof ContextItemSchema>;

export const ContextPackSchema = z.object({
  items: z.array(ContextItemSchema).default([]),
  totalBytes: z.number().default(0),
  maxBytes: z.number().default(CONTEXT_MAX_BYTES),
});
export type ContextPack = z.infer<typeof ContextPackSchema>;

export const ContextConfigSchema = z.object({
  /** CLOSED: anything that is not `active` is `paused`. */
  status: z.string().default("paused"),
  aggressiveness: z.string().default("balanced"),
  updatedAt: z.string().nullable().default(null),
});
export type ContextConfig = z.infer<typeof ContextConfigSchema>;

export const ContextSnapshotSchema = z.object({
  id: z.string(),
  conversationId: z.string().default(""),
  includedContextItemIds: z.array(z.string()).default([]),
  createdAt: z.string().nullable().default(null),
  totalBytes: z.number().default(0),
});
export type ContextSnapshot = z.infer<typeof ContextSnapshotSchema>;

// ── Outcomes: truths, decisions, plans, runs, usage (S8) ─────────────────────

export const DerivedTruthSchema = z.object({
  id: z.string(),
  conversationId: z.string().default(""),
  claim: z.string().default(""),
  confidence: z.number().default(0),
  confidenceBasis: z.string().default(""),
  supersededById: z.string().nullable().default(null),
  /** The REQUEST field is `messageIds`; the response calls the same thing `provenance`. */
  provenance: z.array(z.string()).default([]),
  createdAt: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null),
});
export type DerivedTruth = z.infer<typeof DerivedTruthSchema>;

/** The crystallization fact, projected by the turn face. */
export const DECISION_STATUSES = ["Recorded", "Reversed", "Superseded"] as const;
/** The EXECUTION state, projected only by `conversation-bdi`. Two machines, both real. */
export const DECISION_LIFECYCLES = [
  "None",
  "Proposed",
  "PendingApproval",
  "Active",
  "Done",
  "Dropped",
] as const;
export type DecisionLifecycle = (typeof DECISION_LIFECYCLES)[number];

export const DecisionSchema = z.object({
  id: z.string(),
  conversationId: z.string().default(""),
  statement: z.string().default(""),
  owner: z.string().nullable().default(null),
  rationale: z.string().default(""),
  reversible: z.boolean().default(true),
  status: z.string().default("Recorded"),
  lifecycle: z.string().nullable().default(null),
  provenance: z.array(z.string()).default([]),
  createdAt: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null),
});
export type Decision = z.infer<typeof DecisionSchema>;

export const OutcomesSchema = z.object({
  conversationId: z.string().default(""),
  derivedTruths: z.array(DerivedTruthSchema).default([]),
  decisions: z.array(DecisionSchema).default([]),
});
export type Outcomes = z.infer<typeof OutcomesSchema>;

export const TRIGGER_KINDS = ["now", "at", "every", "on"] as const;
export type TriggerKind = (typeof TRIGGER_KINDS)[number];

export interface DecisionTrigger {
  kind: TriggerKind;
  at?: string;
  recurrence?: string;
  onEvent?: string;
}

/** `runId`, not `id` — the one row in the wave that names its key differently (W8-28). */
export const BdiRunSchema = z.object({
  runId: z.string(),
  decisionId: z.string().nullable().default(null),
  status: z.string().default("Requested"),
  flowRunId: z.string().nullable().default(null),
  correlationId: z.string().nullable().default(null),
  result: z.unknown().nullable().default(null),
  createdAt: z.string().nullable().default(null),
});
export type BdiRun = z.infer<typeof BdiRunSchema>;

export const UsageRollupSchema = z.object({
  scope: z.string().default("conversation"),
  tokensIn: z.number().default(0),
  tokensOut: z.number().default(0),
  toolCalls: z.number().default(0),
  cost: z.number().default(0),
  currency: z.string().default("USD"),
  leafCount: z.number().default(0),
});
export type UsageRollup = z.infer<typeof UsageRollupSchema>;

// ── Derived artifacts + the Executive Digest (S8) ────────────────────────────

export const ExecutiveDigestContentSchema = z.object({
  executiveSummary: z.array(z.string()).default([]),
  derivedTruths: z.array(z.string()).default([]),
  decisions: z.array(z.string()).default([]),
  risks: z.array(z.string()).default([]),
  nextActions: z
    .array(
      z.object({
        action: z.string().default(""),
        owner: z.string().nullable().default(null),
        dueDate: z.string().nullable().default(null),
      }),
    )
    .default([]),
  references: z
    .array(z.object({ label: z.string().default(""), url: z.string().default("") }))
    .default([]),
});
export type ExecutiveDigestContent = z.infer<typeof ExecutiveDigestContentSchema>;

export const DerivedArtifactSchema = z.object({
  id: z.string(),
  conversationId: z.string().default(""),
  type: z.string().default("ed_section"),
  renderer: z.string().default("markdown"),
  title: z.string().default(""),
  status: z.string().default("fresh"),
  createdAt: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null),
  content: ExecutiveDigestContentSchema.nullable().default(null),
  parts: z
    .array(
      z.object({
        id: z.string().default(""),
        kind: z.string().default("text"),
        order: z.number().default(0),
        text: z.string().default(""),
        attachmentId: z.string().nullable().default(null),
        contentType: z.string().nullable().default(null),
        displayName: z.string().nullable().default(null),
      }),
    )
    .default([]),
});
export type DerivedArtifact = z.infer<typeof DerivedArtifactSchema>;

// ── Source traces (S7) ───────────────────────────────────────────────────────

export const SourceRefSchema = z.object({
  url: z.string().default(""),
  title: z.string().nullable().default(null),
  kind: z.string().nullable().default(null),
});
export type SourceRef = z.infer<typeof SourceRefSchema>;

export const SourceTraceSchema = z.object({
  id: z.string(),
  thoughtId: z.string().nullable().default(null),
  streamId: z.string().nullable().default(null),
  thinkerId: z.string().default(""),
  thinkerName: z.string().nullable().default(null),
  tool: z.string().default(""),
  engine: z.string().nullable().default(null),
  query: z.string().nullable().default(null),
  requestUrl: z.string().nullable().default(null),
  status: z.string().default("ok"),
  createdAt: z.string().nullable().default(null),
  sourceCount: z.number().default(0),
  sources: z.array(SourceRefSchema).default([]),
});
export type SourceTrace = z.infer<typeof SourceTraceSchema>;

// ── Skills (S7) ──────────────────────────────────────────────────────────────

export type SkillKind = "document-write" | "document-create" | "document-rename" | "read";
export type SkillMode = "append" | "insert" | "replace_selection" | "replace_document";

export interface SkillDescriptor {
  label: string;
  icon: string;
  kind: SkillKind;
  defaultMode?: SkillMode;
}

/**
 * The 12 skills the server's `SkillsPromptSection` teaches the model to emit. There is no
 * description and no prompt here on purpose — the prompt lives server-side, and a second copy
 * in the client would drift.
 */
export const SKILL_CATALOG: Record<string, SkillDescriptor> = {
  write_document: {
    label: "Write to document",
    icon: "✍️",
    kind: "document-write",
    defaultMode: "append",
  },
  edit_selection: {
    label: "Edit selection",
    icon: "✏️",
    kind: "document-write",
    defaultMode: "replace_selection",
  },
  insert_section: {
    label: "Insert section",
    icon: "➕",
    kind: "document-write",
    defaultMode: "append",
  },
  insert_table: { label: "Insert table", icon: "▦", kind: "document-write", defaultMode: "append" },
  insert_diagram: {
    label: "Insert diagram",
    icon: "📊",
    kind: "document-write",
    defaultMode: "append",
  },
  create_document: { label: "Create document", icon: "🆕", kind: "document-create" },
  rename_document: { label: "Rename document", icon: "🏷", kind: "document-rename" },
  search_documents: { label: "Searched documents", icon: "🔍", kind: "read" },
  read_document: { label: "Read document", icon: "📄", kind: "read" },
  list_collection: { label: "Listed collection", icon: "🗂", kind: "read" },
  list_mcp_tools: { label: "Listed MCP tools", icon: "🧰", kind: "read" },
  call_mcp_tool: { label: "Called MCP tool", icon: "🔌", kind: "read" },
};

export interface SkillInvocation {
  /** `${messageId}:${index}` — stable across re-renders of the same finalised message. */
  id: string;
  skillId: string;
  params: Record<string, string>;
  body: string;
  descriptor: SkillDescriptor | null;
}

// ── Composer taxonomy (S4) ───────────────────────────────────────────────────

export const PROMPT_MESSAGE_TYPES = [
  "ask",
  "instruct",
  "critique",
  "decide",
  "summarize",
  "checklist",
] as const;
export type PromptMessageType = (typeof PROMPT_MESSAGE_TYPES)[number];

export const PROMPT_OUTPUT_FORMATS = [
  "freeform",
  "bullets",
  "checklist",
  "decision_log",
  "markdown",
] as const;
export type PromptOutputFormat = (typeof PROMPT_OUTPUT_FORMATS)[number];

export const THOUGHT_LEVELS = ["low", "medium", "high"] as const;
export type ThoughtLevel = (typeof THOUGHT_LEVELS)[number];

export const FACETS = ["Design", "Decisions", "Risks", "TODO"] as const;
export type Facet = (typeof FACETS)[number];

// ── Document AI settings + resolved document thinker (S5) ────────────────────

export const DocumentAiSettingsSchema = z.object({
  documentId: z.string().default(""),
  assignedThinkerIds: z.array(z.string()).default([]),
  defaultContextItemIds: z.array(z.string()).default([]),
  aiAssistanceEnabled: z.boolean().default(true),
});
export type DocumentAiSettings = z.infer<typeof DocumentAiSettingsSchema>;

/** `explicit` · `collection` · `team-local-default` · `system-default` · `user-pin` (§B.4). */
export const ThinkerOptionSchema = z.object({
  thinkerId: z.string(),
  displayName: z.string().default(""),
  kind: z.string().default("cloud"),
  machineId: z.string().nullable().default(null),
  workspacePath: z.string().nullable().default(null),
  isOnline: z.boolean().nullable().default(null),
  usable: z.boolean().nullable().default(null),
  reason: z.string().nullable().default(null),
});
export type ThinkerOption = z.infer<typeof ThinkerOptionSchema>;

export const DocumentThinkerResolutionSchema = z.object({
  collectionId: z.string().nullable().default(null),
  resolved: z
    .object({
      thinkerId: z.string().nullable().default(null),
      displayName: z.string().default(""),
      source: z.string().default("system-default"),
      fallbackFrom: z.string().nullable().default(null),
      redacted: z.boolean().nullable().default(null),
    })
    .nullable()
    .default(null),
  options: z.array(ThinkerOptionSchema).default([]),
});
export type DocumentThinkerResolution = z.infer<typeof DocumentThinkerResolutionSchema>;

// ── Drafts (request shapes) ──────────────────────────────────────────────────

/** `id` is CLIENT-MINTABLE and S10 depends on it: the comment-thread id IS the conversation id. */
export interface StartConversationDraft {
  id?: string;
  documentId: string;
  title: string;
  initialMessage?: string;
  createdBy?: string;
  priority?: ThoughtPriority;
  tags?: string[];
  anchors?: Array<Partial<Anchor>>;
}

export interface CreateConversationDraft {
  documentId?: string;
  title?: string;
  anchorType?: string;
  anchorRef?: string;
  selectedText?: string;
  thinkerIds?: string[];
  initialMessage?: string;
  flowId?: string;
}

export interface MessageDraft {
  content: string;
  author?: string;
  authorType?: AuthorType;
  messageType?: string;
  metadata?: Record<string, unknown>;
  targetThinkerIds?: string[];
}

export interface RevisePatch {
  content: string;
  editedById?: string;
  editedByName?: string;
  reason?: string;
  markDownstreamStale?: boolean;
}

export interface ReviseResult {
  /** `null` on the no-op path — indistinguishable from failure except by the stale lists. */
  revisionId: string | null;
  revision: number;
  staleMessageIds: string[];
  staleArtifactIds: string[];
}

export interface RegenerateResult {
  conversationId: string;
  edContent: ExecutiveDigestContent | null;
  clearedMessageIds: string[];
  updatedArtifactIds: string[];
}

export interface CommitResult {
  conversationId: string;
  artifactId: string | null;
  status: string;
  committedAt: string | null;
  title: string | null;
  edContent: ExecutiveDigestContent | null;
  moderatorSynthesis: string | null;
}

export interface FlattenResult {
  conversationId: string;
  content: string;
  format: string;
  moderatorSynthesis: string | null;
  sourceMessageIds: string[];
}

export interface CompanionResult {
  knowItDocumentId: string | null;
  markdown: string;
}

export interface RethinkResult {
  anchorContent: string;
  droppedCount: number;
  droppedMessages: ConversationMessage[];
}

export interface ConversationSearch {
  search?: string;
  resolved?: boolean;
  priority?: ThoughtPriority;
  limit?: number;
}

export interface ConversationStats {
  totalThoughts: number;
  resolvedThoughts: number;
  unresolvedThoughts: number;
  totalMessages: number;
  priorityBreakdown: Record<string, number>;
}

export interface ConversationsOverview {
  totalCount: number;
  activeCount: number;
  /** Hardcoded 0 server-side — never render it as "nothing is processing". */
  processingCount: number;
  recentConversations: ConversationSummary[];
  byDocument: Array<{
    documentId: string;
    title: string;
    conversationCount: number;
    lastUpdated: string | null;
  }>;
}

export interface PromptRunDraft {
  content: string;
  promptType?: PromptMessageType;
  outputFormat?: PromptOutputFormat;
  thoughtLevel?: ThoughtLevel;
  facetTags?: string[];
  broadcast?: boolean;
  targetThinkerIds?: string[];
  contextItemIds?: string[];
  author?: string;
}

export interface ThinkerDisposition {
  action: "delete" | "merge";
  mergeToThinkerId?: string;
}

export interface ThinkerRemovalResult {
  thinkerIds: string[];
  messagesAffected: number;
  action: string;
}

export interface ContextItemDraft {
  type?: ContextItemType;
  title?: string;
  content: string;
  source?: Record<string, unknown>;
  facetTags?: string[];
  audience?: Record<string, unknown>;
  addedBy?: string;
  status?: ContextStatus;
  createdById?: string;
}

export interface TruthDraft {
  claim: string;
  confidence?: number;
  confidenceBasis?: string;
  messageIds: string[];
}

export interface DecisionDraft {
  statement: string;
  owner?: string;
  rationale?: string;
  reversible?: boolean;
  messageIds: string[];
}

export interface PlanDraft {
  name?: string;
  kind?: "task" | "flow";
  flowDefinitionId?: string;
  taskSpec?: Record<string, unknown>;
  messageIds?: string[];
}

export interface BindDraft {
  planId: string;
  trigger: DecisionTrigger;
  autonomyLevel?: string;
  thinkerInitiated?: boolean;
}

export type DecisionAction = "approve" | "deny" | "pause" | "resume" | "cancel";
